import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'app-theme';

const THEMES = {
  colorful: {
    id: 'colorful',
    icon: '🎨',
    pageBg: 'bg-gradient-to-br from-blue-50 to-indigo-100',
    headerBorder: 'border-indigo-600',
    headerAccent: 'text-indigo-600',
    headerAccentHover: 'hover:text-indigo-800',
    cardBorder: 'border-gray-100',
    cardHoverBorder: 'hover:border-indigo-300',
    sectionLink: 'text-indigo-600 hover:text-indigo-700',
    activePill: 'bg-indigo-100 text-indigo-700',
    buttonPrimary: 'bg-indigo-600 hover:bg-indigo-700',
    backButton: 'text-indigo-600 hover:text-indigo-800',
    actionPrimary: 'from-green-500 to-emerald-600',
    actionSecondary: 'from-yellow-400 to-orange-500',
    actionDanger: 'from-red-500 to-rose-600',
    accentLight: 'bg-indigo-50',
    accentLightGradient: 'from-indigo-50 to-purple-50',
    accentBorder: 'border-indigo-200',
    accentHoverBg: 'hover:bg-indigo-50',
    accentHoverBorder: 'hover:border-indigo-300',
    accentHoverText: 'group-hover:text-indigo-700',
    badgeBg: 'bg-indigo-100',
    badgeText: 'text-indigo-700',
    successBg: 'bg-green-50',
    successText: 'text-green-700',
    successBorder: 'border-green-300',
    successIconBg: 'bg-green-500',
    successHoverBg: 'hover:bg-green-200',
    infoBg: 'bg-blue-100',
    infoText: 'text-blue-700',
    infoBorder: 'border-blue-300',
    infoHoverBg: 'hover:bg-blue-200',
    warningBg: 'bg-yellow-100',
    warningText: 'text-yellow-700',
    hiitBadgeBg: 'bg-orange-100',
    hiitBadgeText: 'text-orange-700',
    neutralPageBg: 'bg-gray-100',
    neutralBg: 'bg-gray-50',
    neutralText: 'text-gray-500',
    neutralBorder: 'border-gray-200',
    neutralTitle: 'text-gray-800',
    neutralMuted: 'text-gray-600',
    progressTrack: 'bg-white/20',
    cardBg: 'bg-white',
    headerButtonHover: 'hover:bg-white/20',
    headerSubtitle: 'text-white/80',
    dangerBg: 'bg-red-100',
    dangerText: 'text-red-700',
    dangerBorder: 'border-red-300',
    dangerHoverBg: 'hover:bg-red-50',
    gradients: [
      'from-indigo-500 to-indigo-600',
      'from-purple-500 to-pink-600',
      'from-green-500 to-emerald-600',
      'from-orange-500 to-red-600',
      'from-blue-500 to-indigo-600',
      'from-blue-500 to-cyan-600',
      'from-red-500 to-pink-600',
      'from-indigo-500 to-purple-600',
      'from-yellow-500 to-orange-600',
      'from-teal-500 to-green-600',
      'from-emerald-500 to-green-600',
      'from-cyan-500 to-blue-600',
    ],
  },
  professional: {
    id: 'professional',
    icon: '💼',
    pageBg: 'bg-gradient-to-br from-slate-50 via-slate-100 to-blue-50/50',
    headerBorder: 'border-blue-800',
    headerAccent: 'text-blue-800',
    headerAccentHover: 'hover:text-blue-900',
    cardBorder: 'border-slate-200',
    cardHoverBorder: 'hover:border-blue-300',
    sectionLink: 'text-blue-700 hover:text-blue-800',
    activePill: 'bg-blue-100 text-blue-800',
    buttonPrimary: 'bg-blue-700 hover:bg-blue-800',
    backButton: 'text-slate-700 hover:text-slate-900',
    actionPrimary: 'from-blue-700 to-blue-800',
    actionSecondary: 'from-slate-500 to-slate-600',
    actionDanger: 'from-red-600 to-red-700',
    accentLight: 'bg-slate-100',
    accentLightGradient: 'from-slate-50 to-blue-50/50',
    accentBorder: 'border-slate-200',
    accentHoverBg: 'hover:bg-slate-100',
    accentHoverBorder: 'hover:border-slate-300',
    accentHoverText: 'group-hover:text-blue-800',
    badgeBg: 'bg-slate-200/80',
    badgeText: 'text-slate-800',
    successBg: 'bg-emerald-50',
    successText: 'text-emerald-700',
    successBorder: 'border-emerald-300',
    successIconBg: 'bg-emerald-600',
    successHoverBg: 'hover:bg-emerald-200',
    infoBg: 'bg-blue-100',
    infoText: 'text-blue-700',
    infoBorder: 'border-blue-300',
    infoHoverBg: 'hover:bg-blue-200',
    warningBg: 'bg-amber-100',
    warningText: 'text-amber-700',
    hiitBadgeBg: 'bg-orange-100',
    hiitBadgeText: 'text-orange-700',
    neutralPageBg: 'bg-slate-100',
    neutralBg: 'bg-slate-50',
    neutralText: 'text-slate-500',
    neutralBorder: 'border-slate-200',
    neutralTitle: 'text-slate-800',
    neutralMuted: 'text-slate-600',
    progressTrack: 'bg-white/20',
    cardBg: 'bg-white',
    headerButtonHover: 'hover:bg-white/20',
    headerSubtitle: 'text-white/80',
    dangerBg: 'bg-red-100',
    dangerText: 'text-red-700',
    dangerBorder: 'border-red-300',
    dangerHoverBg: 'hover:bg-red-50',
    gradients: [
      'from-slate-600 to-slate-700',
      'from-blue-700 to-blue-800',
      'from-slate-600 to-blue-700',
      'from-blue-600 to-indigo-700',
      'from-slate-500 to-slate-600',
    ],
  },
  industrial: {
    id: 'industrial',
    icon: '⚙️',
    pageBg: 'bg-gradient-to-br from-stone-200 via-zinc-100 to-stone-300',
    headerBorder: 'border-zinc-800',
    headerAccent: 'text-zinc-800',
    headerAccentHover: 'hover:text-zinc-900',
    cardBorder: 'border-stone-300',
    cardHoverBorder: 'hover:border-amber-500/60',
    sectionLink: 'text-amber-700 hover:text-amber-800',
    activePill: 'bg-amber-100 text-amber-800',
    buttonPrimary: 'bg-amber-600 hover:bg-amber-700',
    backButton: 'text-amber-700 hover:text-amber-900',
    actionPrimary: 'from-amber-600 to-amber-700',
    actionSecondary: 'from-zinc-600 to-zinc-700',
    actionDanger: 'from-red-700 to-red-800',
    accentLight: 'bg-amber-50/80',
    accentLightGradient: 'from-amber-50/80 to-stone-100',
    accentBorder: 'border-amber-200',
    accentHoverBg: 'hover:bg-amber-50',
    accentHoverBorder: 'hover:border-amber-400',
    accentHoverText: 'group-hover:text-amber-800',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-800',
    successBg: 'bg-emerald-50',
    successText: 'text-emerald-700',
    successBorder: 'border-emerald-300',
    successIconBg: 'bg-emerald-600',
    successHoverBg: 'hover:bg-emerald-200',
    infoBg: 'bg-amber-100',
    infoText: 'text-amber-800',
    infoBorder: 'border-amber-300',
    infoHoverBg: 'hover:bg-amber-200',
    warningBg: 'bg-amber-100',
    warningText: 'text-amber-800',
    hiitBadgeBg: 'bg-orange-100',
    hiitBadgeText: 'text-orange-700',
    neutralPageBg: 'bg-stone-100',
    neutralBg: 'bg-stone-50',
    neutralText: 'text-stone-500',
    neutralBorder: 'border-stone-200',
    neutralTitle: 'text-stone-800',
    neutralMuted: 'text-stone-600',
    progressTrack: 'bg-white/20',
    cardBg: 'bg-white',
    headerButtonHover: 'hover:bg-white/20',
    headerSubtitle: 'text-white/80',
    dangerBg: 'bg-red-100',
    dangerText: 'text-red-700',
    dangerBorder: 'border-red-300',
    dangerHoverBg: 'hover:bg-red-50',
    gradients: [
      'from-zinc-700 to-zinc-800',
      'from-amber-600 to-amber-700',
      'from-stone-600 to-zinc-700',
      'from-amber-500 to-orange-600',
      'from-zinc-600 to-stone-700',
      'from-stone-500 to-amber-600',
    ],
  },
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'colorful';
    } catch {
      return 'colorful';
    }
  });

  const theme = THEMES[themeId] || THEMES.colorful;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, themeId);
    } catch { /* non-blocking */ }
  }, [themeId]);

  const setTheme = useCallback((id) => {
    if (THEMES[id]) setThemeId(id);
  }, []);

  const getGradient = useCallback((index) => {
    const g = theme.gradients;
    return g[index % g.length];
  }, [theme.gradients]);

  return (
    <ThemeContext.Provider value={{ theme, themeId, setTheme, getGradient, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      theme: THEMES.colorful,
      themeId: 'colorful',
      setTheme: () => {},
      getGradient: (i) => THEMES.colorful.gradients[i % THEMES.colorful.gradients.length],
      themes: THEMES,
    };
  }
  return ctx;
}

export default ThemeContext;
